import * as _ from 'lodash-es';
import type Quill from 'quill/core';
import type { Range } from 'quill/core';
import { Delta } from 'quill/core';
import getApolloClient from '../../app/apollo/getApolloClient';
import getReduxStore from '../../app/redux/getReduxStore';
import { gql } from '../../graphql/generated/gql';
import { editorToasts } from '../../toasts/toasts';
import { UIActions } from '../../ui/uiSlice';
import TableEmbed from '../formats/TableEmbed';
import type { TableDocumentCell } from '../table/types';
import { SelectionType } from '../table/useTableSelection';
import { stringifyCellIdentity } from '../table/utils';
import deltaToText from './deltaToText';
import generateDeltaRandomId from './generateDeltaRandomId';

const generateTableDelta = (table: TableEmbed): Delta | null => {
  const selection = table.getSelection();
  switch (selection?.type) {
    case SelectionType.Cell: {
      const quill = table.getCellQuill(selection.identity);
      const range = table.getCellLastRange(selection.identity);
      if (quill && range?.length) {
        return quill.getContents(range.index, range.length);
      }

      return new Delta();
    }
    case SelectionType.Table:
    case SelectionType.Row:
    case SelectionType.Column: {
      const deltas = table.mapCellsInRange(selection, (content) => content);
      const rowCount = deltas.length;
      const columnCount = deltas[0]?.length ?? 0;
      if (rowCount <= 0 || columnCount <= 0) return null;
      const cells: { [identity: string]: TableDocumentCell } = {};
      for (let rowIndex = 0; rowIndex < rowCount; rowIndex += 1) {
        for (let columnIndex = 0; columnIndex < columnCount; columnIndex += 1) {
          const cell = deltas[rowIndex]?.[columnIndex];
          if (cell) {
            cells[stringifyCellIdentity(rowIndex, columnIndex)] = {
              content: cell,
            };
          }
        }
      }
      return new Delta().insert({
        'table-embed': {
          cells,
          rows: _.range(rowCount).map(() => ({
            insert: { id: generateDeltaRandomId() },
          })),
          columns: _.range(columnCount).map(() => ({
            insert: { id: generateDeltaRandomId() },
          })),
        },
      });
    }
    default:
      return null;
  }
};

// eslint-disable-next-line import/no-unused-modules -- expose for testing
export const getDeltaForMarkdown = (quill: Quill, range: Range) => {
  if (range.length === 1) {
    const [leaf] = quill.getLeaf(range.index);
    if (leaf instanceof TableEmbed) {
      const tableDelta = generateTableDelta(leaf);
      if (tableDelta) return tableDelta;
    }
  }

  const contents = quill.getContents(range.index, range.length);
  const [lastLine, lastLineOffset] = quill.getLine(range.index + range.length);
  if (!lastLine || lastLineOffset === 0) return contents;
  const lastLineFormats = lastLine.formats();

  return _.isEmpty(lastLineFormats)
    ? contents
    : contents.insert('\n', lastLineFormats);
};

const MARKDOWN_CONTENT_QUERY = gql(/** @complexity 1 */ `
  query markdownContent($delta: String!) {
    markdown: convert(delta: $delta)
  }
`);

const getMarkdownContent = async (
  quill: Quill,
  range: Range,
): Promise<string> => {
  const delta = getDeltaForMarkdown(quill, range);
  const client = getApolloClient();
  if (client) {
    const { data: { markdown } = {} } = await client.query({
      query: MARKDOWN_CONTENT_QUERY,
      variables: { delta: JSON.stringify(delta.ops) },
    });
    if (markdown) return markdown.trim();
  }
  return deltaToText(delta);
};

const copyAsMarkdown = async (quill: Quill, range: Range) => {
  const store = getReduxStore();
  const markdownContentPromise = getMarkdownContent(quill, range);
  try {
    const isSafari = /Version\/.* Safari\//.test(navigator.userAgent);
    if (isSafari) {
      // Prefer using async ClipboardItem to avoid violating user gesture requirements
      // caused by the delay in obtaining markdown content.
      // Currently, only Safari has these requirements (Chrome has looser requirements as they
      // implemented User Activation v2), and only Safari supports
      // async ClipboardItem.
      await navigator.clipboard.write([
        new ClipboardItem({ 'text/plain': markdownContentPromise }),
      ]);
    } else {
      await navigator.clipboard.writeText(await markdownContentPromise);
    }

    store.dispatch(UIActions.pushToast(editorToasts.markdownCopied));
  } catch (error) {
    if (error.message === 'Document is not focused.') {
      store.dispatch(UIActions.pushToast(editorToasts.markdownCopyError));
    }
  }
};

export default copyAsMarkdown;
